/**
 * SettingsSEO — Iter 191l · Search Engine Optimization.
 *
 * Squarespace / Shopify parity SEO hub covering everything a vendor needs to
 * rank well and control how their storefront appears in search & socials:
 *
 *   1. Search appearance — default page title & meta description with a live
 *      Google SERP preview (matches Squarespace's SEO Basics screen).
 *   2. Social sharing — Open Graph title, description, and OG image so links
 *      look great when shared to Facebook/LinkedIn/X/Slack; live card preview.
 *   3. Search-engine verification — Google Search Console, Bing Webmaster,
 *      Pinterest, Yandex (all industry-standard meta-tag verifications).
 *   4. Sitemap + robots — copy-ready sitemap URL, indexing toggle (noindex
 *      pauses all crawlers site-wide), custom robots.txt overrides.
 *   5. Analytics — Google Analytics 4 Measurement ID + Google Tag Manager
 *      Container ID (industry standard; Facebook Pixel lives in General).
 *   6. Structured data — Organization + LocalBusiness schema.org toggles for
 *      rich-result eligibility.
 *
 * Persistence: `settings.seo` object round-tripped via
 * `GET/PUT /api/builder/settings`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Loader2, Save, ExternalLink, Copy, Check, Info,
  Globe, Share2, ShieldCheck, LineChart, Code2, MapPin,
} from 'lucide-react';
import BackToSettings from './_BackToSettings';
import SettingsSEOScore from './SettingsSEOScore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULTS = {
  // 1. Search appearance
  title_template: '{page_title} · {site_name}',
  default_meta_description: '',
  // 2. Social sharing
  og_title: '',
  og_description: '',
  og_image_url: '',
  twitter_handle: '',
  // 3. Verifications
  google_verification: '',
  bing_verification: '',
  pinterest_verification: '',
  yandex_verification: '',
  // 4. Sitemap / robots
  indexable: true,
  robots_extra: '',
  // 5. Analytics
  ga4_measurement_id: '',
  gtm_container_id: '',
  // 6. Structured data
  schema_organization: true,
  schema_local_business: false,
  business_type: 'Store',
  business_street: '',
  business_city: '',
  business_region: '',
  business_postal_code: '',
  business_country: '',
  business_phone: '',
};

/* ------------------------------ Primitives ------------------------------ */
function Toggle({ checked, onChange, disabled = false, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled} data-testid={testid}
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-[#A78BFA]' : 'bg-white/15'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

const Field = ({ label, hint, children, testid }) => (
  <div className="py-4 border-b border-white/[0.06] last:border-b-0" data-testid={testid}>
    <div className="flex items-start justify-between gap-6">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-white">{label}</div>
        {hint && <div className="mt-1 text-[12px] text-[#94A3B8] leading-snug">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  </div>
);

function SectionCard({ title, description, icon: Icon, children, testid }) {
  return (
    <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} className="text-[#A78BFA]" />}
        <h3 className="text-[15px] font-bold text-white tracking-tight">{title}</h3>
      </div>
      {description && <p className="mt-1 text-[12.5px] text-[#94A3B8] leading-snug">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function TextInput({ value, onChange, placeholder, mono, testid, maxLength }) {
  return (
    <input
      type="text" value={value || ''} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
      className={`w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60 ${mono ? 'font-mono' : ''}`}
      data-testid={testid}
    />
  );
}

/* ------------------------------ Previews ------------------------------ */
function SerpPreview({ title, description, url }) {
  const displayTitle = (title || 'Your Page Title').slice(0, 60);
  const displayDesc = (description || 'Your default meta description will appear here — usually 155–160 characters describing what visitors will find on the page.').slice(0, 160);
  return (
    <div className="rounded-lg bg-white p-4 shadow-inner max-w-xl">
      <div className="flex items-center gap-2 text-[12px] text-[#5F6368]">
        <span className="w-4 h-4 rounded-full bg-gradient-to-br from-[#4285F4] via-[#EA4335] to-[#FBBC05]" />
        <span>{url}</span>
      </div>
      <div className="mt-1 text-[18px] leading-tight text-[#1a0dab] hover:underline cursor-pointer" style={{ fontFamily: 'arial, sans-serif' }}>
        {displayTitle}
      </div>
      <div className="mt-1 text-[13px] leading-snug text-[#4d5156]" style={{ fontFamily: 'arial, sans-serif' }}>
        {displayDesc}
      </div>
    </div>
  );
}

function SocialPreview({ title, description, image, url }) {
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-white/[0.02] max-w-md">
      <div className="w-full aspect-[1.9] bg-gradient-to-br from-[#A78BFA]/30 to-[#6D28D9]/20 grid place-items-center overflow-hidden">
        {image ? (
          <img src={image} alt="Social preview" className="w-full h-full object-cover" />
        ) : (
          <div className="text-white/40 text-[11px] uppercase tracking-widest">1200 × 630 · Add OG image</div>
        )}
      </div>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-white/40">{url}</div>
        <div className="mt-1 text-[13px] font-semibold text-white line-clamp-1">{title || 'Your Site Name'}</div>
        <div className="mt-0.5 text-[11.5px] text-white/60 line-clamp-2">
          {description || 'A short description shown when your link is shared on Facebook, LinkedIn, X, Slack, and more.'}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Page --------------------------------- */
export default function SettingsSEO() {
  const [seo, setSeo] = useState(DEFAULTS);
  const [siteMeta, setSiteMeta] = useState({ subdomain: '', brand_name: 'Your Site' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, { headers: authHeaders() });
        const remote = res.data?.seo;
        if (remote && typeof remote === 'object') {
          setSeo({ ...DEFAULTS, ...remote });
        }
        setSiteMeta({
          subdomain: res.data?.subdomain || '',
          brand_name: res.data?.brand_name || 'Your Site',
        });
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const update = (patch) => { setSeo((s) => ({ ...s, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { seo }, { headers: authHeaders() });
      toast.success('SEO settings saved');
      setDirty(false);
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const siteBase = useMemo(() =>
    siteMeta.subdomain ? `https://${siteMeta.subdomain}.entreprenatives.com` : 'https://yourbrand.com',
    [siteMeta.subdomain]);

  const sitemapUrl = `${siteBase}/sitemap.xml`;
  const robotsUrl = `${siteBase}/robots.txt`;

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); };

  return (
    <div className="min-h-full" data-testid="settings-seo-page">
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md" title="Search settings">
            <Search size={14} />
          </button>
          {saving && <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">Saving…</span>}
          <button
            type="button" onClick={save} disabled={!dirty || saving}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] transition-all ${
              dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'
            }`}
            data-testid="seo-save-btn"
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Website</span>
          <span className="mx-2">/</span>
          <span className="text-white">SEO</span>
        </div>

        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Search Engine Optimization
        </h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Control how your site appears in Google, Bing, and social feeds. Everything on this page follows the same
          industry-standard practices Squarespace and Shopify use.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* 0 · SEO SCORE METER */}
            <div className="mt-8">
              <SettingsSEOScore
                seo={{
                  title: seo.title_template || '',
                  description: seo.default_meta_description || '',
                  keywords: seo.keywords || '',
                  og_image: seo.og_image_url || '',
                }}
                settings={{ favicon_url: seo.favicon_url }}
              />
            </div>

            {/* 1 · SEARCH APPEARANCE */}
            <SectionCard
              title="Search appearance"
              description="Set the default site title format and meta description. Individual pages can override these."
              icon={Globe}
              testid="section-search-appearance"
            >
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Site title template</span>
                <TextInput
                  value={seo.title_template}
                  onChange={(v) => update({ title_template: v })}
                  placeholder="{page_title} · {site_name}"
                  mono
                  testid="seo-title-template"
                />
                <p className="mt-1.5 text-[11px] text-[#94A3B8]">
                  Tokens: <span className="font-mono text-white/70">{'{page_title}'}</span>, <span className="font-mono text-white/70">{'{site_name}'}</span>
                </p>
              </label>

              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Default meta description</span>
                <textarea
                  value={seo.default_meta_description}
                  onChange={(e) => update({ default_meta_description: e.target.value })}
                  maxLength={160}
                  rows={3}
                  placeholder="What visitors will find on your site — 150–160 characters is ideal."
                  className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60 resize-y"
                  data-testid="seo-meta-description"
                />
                <p className="mt-1 text-[11px] text-[#94A3B8]">{(seo.default_meta_description || '').length} / 160</p>
              </label>

              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold mb-2">Live Google preview</div>
                <SerpPreview
                  title={(seo.title_template || '').replace('{page_title}', 'Home').replace('{site_name}', siteMeta.brand_name || 'Your Site')}
                  description={seo.default_meta_description}
                  url={siteBase.replace('https://', '') + ' › home'}
                />
              </div>
            </SectionCard>

            {/* 2 · SOCIAL SHARING */}
            <SectionCard
              title="Social sharing"
              description="How your links look when shared on Facebook, LinkedIn, X, Slack, and iMessage."
              icon={Share2}
              testid="section-social-sharing"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Open Graph title</span>
                  <TextInput value={seo.og_title} onChange={(v) => update({ og_title: v })} placeholder="Falls back to page title" testid="seo-og-title" maxLength={70} />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">X (Twitter) handle</span>
                  <TextInput value={seo.twitter_handle} onChange={(v) => update({ twitter_handle: v })} placeholder="@yourbrand" testid="seo-twitter-handle" />
                </label>
              </div>
              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Open Graph description</span>
                <textarea
                  value={seo.og_description} onChange={(e) => update({ og_description: e.target.value })}
                  rows={2} maxLength={200}
                  placeholder="Falls back to meta description if empty."
                  className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60 resize-y"
                  data-testid="seo-og-description"
                />
              </label>
              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Open Graph image URL (1200 × 630)</span>
                <TextInput value={seo.og_image_url} onChange={(v) => update({ og_image_url: v })} placeholder="https://…/social-card.png" testid="seo-og-image" mono />
              </label>

              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold mb-2">Live share preview</div>
                <SocialPreview
                  title={seo.og_title || siteMeta.brand_name}
                  description={seo.og_description || seo.default_meta_description}
                  image={seo.og_image_url}
                  url={siteBase.replace('https://', '').toUpperCase()}
                />
              </div>
            </SectionCard>

            {/* 3 · SEARCH ENGINE VERIFICATION */}
            <SectionCard
              title="Search engine verification"
              description="Paste the verification token each search engine gives you. We inject the meta tag site-wide."
              icon={ShieldCheck}
              testid="section-verification"
            >
              <VerificationRow
                label="Google Search Console"
                hint="Get it at search.google.com/search-console › Add property › HTML tag"
                docsUrl="https://search.google.com/search-console"
                value={seo.google_verification}
                onChange={(v) => update({ google_verification: v })}
                testid="seo-google-verification"
              />
              <VerificationRow
                label="Bing Webmaster Tools"
                hint="At bing.com/webmasters › Add site › HTML meta tag"
                docsUrl="https://www.bing.com/webmasters"
                value={seo.bing_verification}
                onChange={(v) => update({ bing_verification: v })}
                testid="seo-bing-verification"
              />
              <VerificationRow
                label="Pinterest"
                hint="Business account › Claim website › HTML tag"
                docsUrl="https://www.pinterest.com/business/create/"
                value={seo.pinterest_verification}
                onChange={(v) => update({ pinterest_verification: v })}
                testid="seo-pinterest-verification"
              />
              <VerificationRow
                label="Yandex Webmaster"
                hint="At webmaster.yandex.com › Add site › Meta tag"
                docsUrl="https://webmaster.yandex.com/"
                value={seo.yandex_verification}
                onChange={(v) => update({ yandex_verification: v })}
                testid="seo-yandex-verification"
              />
            </SectionCard>

            {/* 4 · SITEMAP + ROBOTS */}
            <SectionCard
              title="Sitemap &amp; indexing"
              description="Submit your sitemap to Google/Bing after setup, and control whether crawlers can index your site."
              icon={Globe}
              testid="section-sitemap"
            >
              <CopyRow label="Sitemap URL" value={sitemapUrl} onCopy={() => copy(sitemapUrl)} testid="seo-sitemap-url" />
              <CopyRow label="Robots URL" value={robotsUrl} onCopy={() => copy(robotsUrl)} testid="seo-robots-url" />

              <Field
                label="Allow search engines to index this site"
                hint="Turn off to keep your site out of Google (useful during a private beta). Adds a site-wide noindex tag."
                testid="field-indexable"
              >
                <Toggle checked={seo.indexable} onChange={(v) => update({ indexable: v })} testid="seo-indexable-toggle" />
              </Field>

              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Custom robots.txt rules (advanced)</span>
                <textarea
                  value={seo.robots_extra} onChange={(e) => update({ robots_extra: e.target.value })}
                  rows={4}
                  placeholder="User-agent: GPTBot&#10;Disallow: /"
                  className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[12px] text-white font-mono placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60 resize-y"
                  data-testid="seo-robots-extra"
                />
                <p className="mt-1 text-[11px] text-[#94A3B8]">
                  Appended to the auto-generated robots.txt. Useful to block AI scrapers like GPTBot, ClaudeBot, CCBot.
                </p>
              </label>
            </SectionCard>

            {/* 5 · ANALYTICS */}
            <SectionCard
              title="Analytics"
              description="Track visitors with Google Analytics 4 and Google Tag Manager. IDs are injected into every page."
              icon={LineChart}
              testid="section-analytics"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Google Analytics 4 (Measurement ID)</span>
                  <TextInput value={seo.ga4_measurement_id} onChange={(v) => update({ ga4_measurement_id: v.trim() })} placeholder="G-XXXXXXXXXX" testid="seo-ga4" mono />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Google Tag Manager (Container ID)</span>
                  <TextInput value={seo.gtm_container_id} onChange={(v) => update({ gtm_container_id: v.trim() })} placeholder="GTM-XXXXXX" testid="seo-gtm" mono />
                </label>
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
                <Info size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
                <p className="text-[11.5px] text-white/75 leading-snug">
                  Meta Pixel &amp; custom scripts live in{' '}
                  <a href="/website-builder/settings/general" className="underline text-[#A78BFA] hover:text-white">General → Advanced</a>. Analytics events fire only after the visitor accepts cookies.
                </p>
              </div>
            </SectionCard>

            {/* 6 · STRUCTURED DATA */}
            <SectionCard
              title="Structured data"
              description="Help Google surface rich results (star ratings, business hours, address, product pricing). We emit valid JSON-LD schema.org markup."
              icon={Code2}
              testid="section-structured-data"
            >
              <Field
                label="Emit Organization schema"
                hint="Recommended. Tells Google your brand name, logo, and social profiles."
                testid="field-schema-org"
              >
                <Toggle checked={seo.schema_organization} onChange={(v) => update({ schema_organization: v })} testid="seo-schema-org" />
              </Field>
              <Field
                label="Emit LocalBusiness schema"
                hint="Turn on if you have a physical storefront or serve a specific area. Enables Google Maps rich results."
                testid="field-schema-local"
              >
                <Toggle checked={seo.schema_local_business} onChange={(v) => update({ schema_local_business: v })} testid="seo-schema-local" />
              </Field>

              {seo.schema_local_business && (
                <div className="mt-4 rounded-lg bg-white/[0.02] border border-white/[0.06] p-4 space-y-3">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold">
                    <MapPin size={11} /> Business details
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextInput value={seo.business_type} onChange={(v) => update({ business_type: v })} placeholder="Store · Restaurant · Salon · …" testid="seo-biz-type" />
                    <TextInput value={seo.business_phone} onChange={(v) => update({ business_phone: v })} placeholder="+1 555 123 4567" testid="seo-biz-phone" />
                    <TextInput value={seo.business_street} onChange={(v) => update({ business_street: v })} placeholder="Street address" testid="seo-biz-street" />
                    <TextInput value={seo.business_city} onChange={(v) => update({ business_city: v })} placeholder="City" testid="seo-biz-city" />
                    <TextInput value={seo.business_region} onChange={(v) => update({ business_region: v })} placeholder="State / Province" testid="seo-biz-region" />
                    <TextInput value={seo.business_postal_code} onChange={(v) => update({ business_postal_code: v })} placeholder="Postal / ZIP code" testid="seo-biz-postal" />
                    <TextInput value={seo.business_country} onChange={(v) => update({ business_country: v })} placeholder="Country (e.g. CA, US)" testid="seo-biz-country" />
                  </div>
                </div>
              )}
            </SectionCard>

            <div className="h-16" />
          </>
        )}
      </div>
    </div>
  );
}

function CopyRow({ label, value, onCopy, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => { onCopy(); setCopied(true); setTimeout(() => setCopied(false), 1200); };
  return (
    <div className="mb-3 flex items-center gap-2" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold w-24 shrink-0">{label}</span>
      <code className="flex-1 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[12px] text-white/85 font-mono truncate">{value}</code>
      <button
        type="button" onClick={doCopy}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-[11px] font-semibold bg-white/5 border border-white/10 text-white hover:bg-white/10"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}
      </button>
      <a
        href={value} target="_blank" rel="noreferrer"
        className="inline-flex items-center px-2 py-2 rounded-md text-white/60 hover:text-white"
        title="Open"
      >
        <ExternalLink size={12} />
      </a>
    </div>
  );
}

function VerificationRow({ label, hint, docsUrl, value, onChange, testid }) {
  return (
    <div className="py-3 border-b border-white/[0.06] last:border-b-0">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[13px] font-semibold text-white">{label}</div>
          <div className="text-[11.5px] text-[#94A3B8]">{hint}</div>
        </div>
        <a href={docsUrl} target="_blank" rel="noreferrer" className="text-[11px] text-[#A78BFA] hover:text-white inline-flex items-center gap-1">
          Open <ExternalLink size={10} />
        </a>
      </div>
      <TextInput value={value} onChange={onChange} placeholder="Paste the content=” value here" mono testid={testid} />
    </div>
  );
}
